import { useEffect, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { motion } from "framer-motion";
import { toast } from "sonner";
import api, { apiErr } from "@/lib/api";
import { useSubscription } from "@/context/SubscriptionContext";
import { useI18n } from "@/context/I18nContext";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Check, Sparkles, ShieldCheck, Loader2, HeartPulse, RotateCcw } from "lucide-react";

function StatusPill({ sub }) {
  const { t } = useI18n();
  if (!sub) return null;
  let label, tone;
  if (sub.is_admin) { label = t("sub.adminStatus"); tone = "bg-sage/15 text-sage"; }
  else if (sub.status === "active" && sub.cancel_at_period_end) { label = `${t("sub.memberEnds")} ${new Date(sub.subscription_expires_at).toLocaleDateString()}`; tone = "bg-primary/15 text-primary"; }
  else if (sub.status === "active") { label = t("sub.member"); tone = "bg-sage/15 text-sage"; }
  else if (sub.status === "trialing") { label = `${t("sub.trial")} ${sub.trial_days_left} ${sub.trial_days_left === 1 ? t("sub.day") : t("sub.days")} ${t("sub.left")}`; tone = "bg-primary/15 text-primary"; }
  else { label = t("sub.freePlan"); tone = "bg-muted text-muted-foreground"; }
  return <span data-testid="subscription-status-pill" className={`inline-flex items-center gap-1.5 rounded-full px-3 py-1 text-xs font-medium ${tone}`}>{label}</span>;
}

export default function Subscription() {
  const { t } = useI18n();
  const { sub, refresh } = useSubscription();
  const [params, setParams] = useSearchParams();
  const [starting, setStarting] = useState(false);
  const [verifying, setVerifying] = useState(false);
  const polled = useRef(false);

  const FEATURES = [t("sub.f1"), t("sub.f2"), t("sub.f3"), t("sub.f4"), t("sub.f5"), t("sub.f6")];

  const introUsed = sub?.intro_used;
  const [plan, setPlan] = useState("annual");

  useEffect(() => {
    const sessionId = params.get("session_id");
    if (!sessionId || polled.current) return;
    polled.current = true;
    setVerifying(true);

    const poll = async (attempt = 0) => {
      if (attempt >= 6) {
        setVerifying(false);
        toast.error("Still processing your payment. It may take a moment — refresh shortly.");
        return;
      }
      try {
        const { data } = await api.get(`/payments/status/${sessionId}`);
        if (data.payment_status === "paid") {
          await refresh();
          setVerifying(false);
          toast.success(t("sub.toastWelcome"));
          setParams({}, { replace: true });
          return;
        }
        if (data.status === "expired") {
          setVerifying(false);
          toast.error("That checkout session expired. Please try again.");
          setParams({}, { replace: true });
          return;
        }
      } catch {}
      setTimeout(() => poll(attempt + 1), 2000);
    };
    poll();
  }, [params, refresh, setParams, t]);

  const startCheckout = async () => {
    setStarting(true);
    try {
      const { data } = await api.post("/payments/checkout", { origin_url: window.location.origin, plan });
      window.location.href = data.url;
    } catch (e) {
      toast.error(apiErr(e));
      setStarting(false);
    }
  };

  const openPortal = async () => {
    setStarting(true);
    try {
      const { data } = await api.post("/payments/portal", { origin_url: window.location.origin });
      window.location.href = data.url;
    } catch (e) {
      toast.error(apiErr(e));
      setStarting(false);
    }
  };

  const [confirmCancel, setConfirmCancel] = useState(false);
  const isMonthly = sub?.plan_interval === "month";
  const isAnnual = sub?.plan_interval === "year";

  const switchAnnual = async () => {
    setStarting(true);
    try { await api.post("/payments/switch-to-annual"); await refresh(); toast.success(t("sub.toastSwitched")); }
    catch (e) { toast.error(apiErr(e)); }
    finally { setStarting(false); }
  };
  const cancelSub = async () => {
    setConfirmCancel(false); setStarting(true);
    try { await api.post("/payments/cancel"); await refresh(); toast(t("sub.toastCancelled")); }
    catch (e) { toast.error(apiErr(e)); }
    finally { setStarting(false); }
  };
  const resumeSub = async () => {
    setStarting(true);
    try { await api.post("/payments/resume"); await refresh(); toast.success(t("sub.toastResumed")); }
    catch (e) { toast.error(apiErr(e)); }
    finally { setStarting(false); }
  };

  const isFullActive = sub?.is_admin || sub?.status === "active";
  const renewDate = sub?.subscription_expires_at ? new Date(sub.subscription_expires_at).toLocaleDateString() : null;
  const annualPrice = sub?.annual_price ?? 49.99;
  const renewAmount = isAnnual ? `€${annualPrice.toFixed(2)}${t("sub.perYearShort")}` : `€${(sub?.monthly_price ?? 4.99).toFixed(2)}${t("sub.perMonthShort")}`;
  let ctaLabel;
  if (plan === "annual") ctaLabel = `${t("sub.subscribe")} €${annualPrice.toFixed(2)}${t("sub.perYearShort")}`;
  else if (introUsed) ctaLabel = `${t("sub.subscribe")} €${(sub?.monthly_price ?? 4.99).toFixed(2)}${t("sub.perMonthShort")}`;
  else ctaLabel = `${t("sub.tryFirst")} €${(sub?.intro_price ?? 1.99).toFixed(2)}`;

  return (
    <div className="mx-auto max-w-2xl" data-testid="subscription-page">
      <div className="mb-8 flex flex-col items-start gap-3">
        <div className="flex items-center gap-2">
          <div className="flex h-9 w-9 items-center justify-center rounded-full bg-primary animate-breathe">
            <HeartPulse className="h-5 w-5 text-primary-foreground" strokeWidth={1.5} />
          </div>
          <h1 className="font-serif text-3xl">{t("sub.full")}</h1>
        </div>
        <StatusPill sub={sub} />
      </div>

      {verifying && (
        <div className="mb-6 flex items-center gap-3 rounded-2xl border border-primary/30 bg-primary/5 p-4 text-sm" data-testid="payment-verifying">
          <Loader2 className="h-4 w-4 animate-spin text-primary" /> {t("sub.verifying")}
        </div>
      )}

      <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }}
                  className="overflow-hidden rounded-3xl border border-border/60 bg-card/60 backdrop-blur-xl">
        <div className="border-b border-border/50 p-7">
          {isFullActive && !sub?.is_admin ? (
            <>
              <p className="text-sm text-muted-foreground">{t("sub.yourPlan")}</p>
              <div className="mt-1 flex items-baseline gap-2">
                <span className="font-serif text-4xl">{t("sub.full")}</span>
                <span className="text-muted-foreground">· {isAnnual ? t("sub.annual") : t("sub.monthly")}</span>
              </div>
              <p className="mt-2 text-sm text-muted-foreground" data-testid="renewal-note">
                {sub?.cancel_at_period_end
                  ? `${t("sub.cancelledUntil")} ${renewDate}. ${t("sub.willNotRenew")}`
                  : `${t("sub.autoChargePre")} ${renewAmount}${renewDate ? ` ${t("sub.on")} ${renewDate}` : ""}.`}
              </p>
            </>
          ) : sub?.is_admin ? (
            <>
              <p className="text-sm text-muted-foreground">{t("sub.adminAccount")}</p>
              <span className="font-serif text-4xl">{t("sub.fullAccess")}</span>
            </>
          ) : (
            <>
              <div className="mb-5 grid grid-cols-2 gap-2 rounded-2xl bg-muted/50 p-1" data-testid="plan-toggle">
                <button data-testid="plan-monthly" onClick={() => setPlan("monthly")}
                  className={`rounded-xl px-3 py-2 text-sm font-medium transition-colors ${plan === "monthly" ? "bg-card text-foreground shadow" : "text-muted-foreground"}`}>
                  {t("sub.monthly")}
                </button>
                <button data-testid="plan-annual" onClick={() => setPlan("annual")}
                  className={`rounded-xl px-3 py-2 text-sm font-medium transition-colors ${plan === "annual" ? "bg-card text-foreground shadow" : "text-muted-foreground"}`}>
                  {t("sub.annual")} <span className="ml-1 rounded-full bg-sage/20 px-1.5 py-0.5 text-[10px] font-semibold text-sage">{t("sub.twoMonthsFree")}</span>
                </button>
              </div>
              {plan === "annual" ? (
                <>
                  <p className="text-sm text-muted-foreground">{t("sub.annual")}</p>
                  <div className="mt-1 flex items-baseline gap-2">
                    <span className="font-serif text-4xl">€{annualPrice.toFixed(2)}</span>
                    <span className="text-muted-foreground">{t("sub.perYear")}</span>
                  </div>
                  <p className="mt-1 text-sm text-muted-foreground">{t("sub.just")} €{(annualPrice / 12).toFixed(2)}{t("sub.perMonthShort")} · {t("sub.annualSub")}</p>
                </>
              ) : !introUsed ? (
                <>
                  <p className="text-sm text-muted-foreground">{t("sub.monthly")} · {t("sub.introOffer")}</p>
                  <div className="mt-1 flex items-baseline gap-2">
                    <span className="font-serif text-4xl">€1.99</span>
                    <span className="text-muted-foreground">{t("sub.firstMonth")}</span>
                  </div>
                  <p className="mt-1 text-sm text-muted-foreground">{t("sub.thenAuto")}</p>
                </>
              ) : (
                <>
                  <p className="text-sm text-muted-foreground">{t("sub.monthly")}</p>
                  <div className="mt-1 flex items-baseline gap-2">
                    <span className="font-serif text-4xl">€4.99</span>
                    <span className="text-muted-foreground">{t("sub.perMonth")}</span>
                  </div>
                  <p className="mt-1 text-sm text-muted-foreground">{t("sub.autoMonthly")}</p>
                </>
              )}
            </>
          )}
        </div>

        <div className="space-y-3 p-7">
          {FEATURES.map((f) => (
            <div key={f} className="flex items-center gap-3 text-sm">
              <div className="flex h-5 w-5 flex-shrink-0 items-center justify-center rounded-full bg-sage/15">
                <Check className="h-3 w-3 text-sage" strokeWidth={2.5} />
              </div>
              {f}
            </div>
          ))}
        </div>

        <div className="p-7 pt-0">
          {sub?.is_admin ? (
            <div className="rounded-2xl bg-sage/10 p-4 text-center text-sm text-sage" data-testid="subscription-active-note">
              {t("sub.adminNote")}
            </div>
          ) : isFullActive ? (
            <div className="space-y-3" data-testid="subscription-active-note">
              {isMonthly && !sub?.cancel_at_period_end && (
                <div className="rounded-2xl border border-sage/40 bg-sage/5 p-4" data-testid="upgrade-annual-box">
                  <div className="flex items-center justify-between gap-3">
                    <div>
                      <p className="text-sm font-medium">{t("sub.saveAnnual")}</p>
                      <p className="text-xs text-muted-foreground">€{annualPrice.toFixed(2)}{t("sub.perYearShort")} — {t("sub.saveAnnualDesc")}</p>
                    </div>
                    <Button size="sm" className="flex-shrink-0 rounded-full" onClick={switchAnnual} disabled={starting} data-testid="switch-annual-button">
                      {starting ? <Loader2 className="h-4 w-4 animate-spin" /> : t("sub.switchAnnual")}
                    </Button>
                  </div>
                </div>
              )}
              {sub?.cancel_at_period_end ? (
                <Button className="w-full rounded-full gap-1.5" onClick={resumeSub} disabled={starting} data-testid="resume-subscription-button">
                  {starting ? <Loader2 className="h-4 w-4 animate-spin" /> : <RotateCcw className="h-4 w-4" strokeWidth={1.5} />} {t("sub.resume")}
                </Button>
              ) : (
                <Button variant="outline" className="w-full rounded-full text-destructive hover:text-destructive" onClick={() => setConfirmCancel(true)} disabled={starting} data-testid="cancel-subscription-button">
                  {t("sub.cancel")}
                </Button>
              )}
              <button onClick={openPortal} disabled={starting} className="mx-auto block text-xs text-muted-foreground hover:text-foreground" data-testid="manage-subscription-button">
                {t("sub.manageBilling")}
              </button>
            </div>
          ) : (
            <Button className="w-full rounded-full" size="lg" onClick={startCheckout} disabled={starting || verifying} data-testid="subscribe-button">
              {starting ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Sparkles className="mr-2 h-4 w-4" strokeWidth={1.5} />}
              {ctaLabel}
            </Button>
          )}
          <p className="mt-4 flex items-center justify-center gap-1.5 text-xs text-muted-foreground">
            <ShieldCheck className="h-3.5 w-3.5" strokeWidth={1.5} /> {t("sub.secure")}
          </p>
        </div>
      </motion.div>

      <p className="mt-6 text-center text-xs text-muted-foreground">
        {t("sub.disclaimer")}
      </p>

      <Dialog open={confirmCancel} onOpenChange={setConfirmCancel}>
        <DialogContent className="max-w-md rounded-3xl" data-testid="cancel-confirm-dialog">
          <DialogHeader>
            <DialogTitle className="font-serif text-xl">{t("sub.cancelTitle")}</DialogTitle>
            <DialogDescription>
              {t("sub.cancelDesc")}
            </DialogDescription>
          </DialogHeader>
          <DialogFooter className="flex-col gap-2 sm:flex-row">
            <Button variant="outline" className="w-full rounded-full" onClick={() => setConfirmCancel(false)} data-testid="cancel-dismiss-button">{t("sub.keepPlan")}</Button>
            <Button variant="destructive" className="w-full rounded-full" onClick={cancelSub} data-testid="cancel-confirm-button">{t("sub.yesCancel")}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
